import { newId } from './ids';
import type {
  VaultHook,
  VaultMarketingAsset,
  VaultMetadata,
  VaultPack,
  VaultPreset,
  VaultPrompt,
  VaultStructure,
} from '../types/domain';

const JSON_FILE_RE = /\.(json|txt)$/i;
const DISPLAY_DATE = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});
const DEFAULT_VERSION = '1.0';
const DEFAULT_AUTHOR = 'Lyrixsa Team';

type VaultFactoryOptions = {
  preserveId?: boolean;
};

type VaultPackFactoryOptions = VaultFactoryOptions & {
  nestedIds?: VaultFactoryOptions;
};

type VaultNormalizeOptions = {
  reassignIds?: boolean;
  preserveCurrentPackId?: boolean;
};

function text(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function objectLike(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
}

function numberValue(value: unknown, fallback = 0): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return fallback;
}

function displayDate(): string {
  return DISPLAY_DATE.format(new Date());
}

function resolvedId(value: unknown, options: VaultFactoryOptions = {}): string {
  const current = text(value).trim();
  if (options.preserveId !== false && current) return current;
  return newId();
}

function normalizeArray<T extends { id: string }>(
  value: unknown,
  createItem: (item?: Partial<T>, options?: VaultFactoryOptions) => T,
  options?: VaultFactoryOptions
): T[] {
  if (!Array.isArray(value)) return [];
  return value.filter(objectLike).map((item) => createItem(item as Partial<T>, options));
}

export function createEmptyVaultPrompt(
  item: Partial<VaultPrompt> = {},
  options?: VaultFactoryOptions
): VaultPrompt {
  return {
    id: resolvedId(item.id, options),
    title: text(item.title),
    genre: text(item.genre),
    mood: text(item.mood),
    difficulty: text(item.difficulty),
    text: text(item.text),
  };
}

export function createEmptyVaultPreset(
  item: Partial<VaultPreset> = {},
  options?: VaultFactoryOptions
): VaultPreset {
  return {
    id: resolvedId(item.id, options),
    name: text(item.name),
    tempo: text(item.tempo),
    mood: text(item.mood),
    energy: numberValue(item.energy),
    vocals: text(item.vocals),
    structure: text(item.structure),
  };
}

export function createEmptyVaultStructure(
  item: Partial<VaultStructure> = {},
  options?: VaultFactoryOptions
): VaultStructure {
  return {
    id: resolvedId(item.id, options),
    name: text(item.name),
    layout: text(item.layout),
  };
}

export function createEmptyVaultHook(
  item: Partial<VaultHook> = {},
  options?: VaultFactoryOptions
): VaultHook {
  return {
    id: resolvedId(item.id, options),
    text: text(item.text),
    genre: text(item.genre),
  };
}

export function createEmptyVaultMetadata(
  item: Partial<VaultMetadata> = {},
  options?: VaultFactoryOptions
): VaultMetadata {
  return {
    id: resolvedId(item.id, options),
    platform: text(item.platform),
    title: text(item.title),
    text: text(item.text),
  };
}

export function createEmptyVaultMarketingAsset(
  item: Partial<VaultMarketingAsset> = {},
  options?: VaultFactoryOptions
): VaultMarketingAsset {
  return {
    id: resolvedId(item.id, options),
    platform: text(item.platform),
    title: text(item.title),
    text: text(item.text),
  };
}

export function createEmptyVaultPack(
  item: Partial<VaultPack> = {},
  options: VaultPackFactoryOptions = {}
): VaultPack {
  const today = displayDate();
  const releaseDate = text(item.releaseDate).trim() || today;
  const nestedIdOptions = options.nestedIds ?? options;

  return {
    id: resolvedId(item.id, options),
    name: text(item.name),
    category: text(item.category),
    description: text(item.description),
    author: text(item.author).trim() || DEFAULT_AUTHOR,
    version: text(item.version).trim() || DEFAULT_VERSION,
    releaseDate,
    updated: text(item.updated).trim() || releaseDate,
    published: typeof item.published === 'boolean' ? item.published : true,
    prompts: normalizeArray(item.prompts, createEmptyVaultPrompt, nestedIdOptions),
    presets: normalizeArray(item.presets, createEmptyVaultPreset, nestedIdOptions),
    structures: normalizeArray(item.structures, createEmptyVaultStructure, nestedIdOptions),
    hooks: normalizeArray(item.hooks, createEmptyVaultHook, nestedIdOptions),
    metadata: normalizeArray(item.metadata, createEmptyVaultMetadata, nestedIdOptions),
    marketing: normalizeArray(item.marketing, createEmptyVaultMarketingAsset, nestedIdOptions),
  };
}

export function createVaultPackJsonTemplate(): VaultPack {
  const template = {
    prompts: [{} as Partial<VaultPrompt>],
    presets: [{} as Partial<VaultPreset>],
    structures: [{} as Partial<VaultStructure>],
    hooks: [{} as Partial<VaultHook>],
    metadata: [{} as Partial<VaultMetadata>],
    marketing: [{} as Partial<VaultMarketingAsset>],
  } as Partial<VaultPack>;

  return createEmptyVaultPack(template, {
    preserveId: false,
    nestedIds: { preserveId: false },
  });
}

export function normalizeVaultPack(
  input: Partial<VaultPack>,
  current?: VaultPack | null,
  options: VaultNormalizeOptions = {}
): VaultPack {
  const keepCurrentPackId = options.reassignIds ? options.preserveCurrentPackId === true : true;

  return createEmptyVaultPack(
    {
      id: options.reassignIds
        ? keepCurrentPackId
          ? current?.id
          : undefined
        : text(input.id).trim() || current?.id,
      name: typeof input.name === 'string' ? input.name : current?.name,
      category: typeof input.category === 'string' ? input.category : current?.category,
      description: typeof input.description === 'string' ? input.description : current?.description,
      author: typeof input.author === 'string' ? input.author : current?.author,
      version: text(input.version).trim() || current?.version,
      releaseDate: text(input.releaseDate).trim() || current?.releaseDate,
      updated: typeof input.updated === 'string' ? input.updated : current?.updated,
      published: typeof input.published === 'boolean' ? input.published : current?.published,
      prompts: Array.isArray(input.prompts) ? input.prompts : current?.prompts,
      presets: Array.isArray(input.presets) ? input.presets : current?.presets,
      structures: Array.isArray(input.structures) ? input.structures : current?.structures,
      hooks: Array.isArray(input.hooks) ? input.hooks : current?.hooks,
      metadata: Array.isArray(input.metadata) ? input.metadata : current?.metadata,
      marketing: Array.isArray(input.marketing) ? input.marketing : current?.marketing,
    },
    {
      preserveId: !options.reassignIds || keepCurrentPackId,
      nestedIds: { preserveId: !options.reassignIds },
    }
  );
}

export function formatVaultPackJson(pack: VaultPack): string {
  return `${JSON.stringify(pack, null, 2)}\n`;
}

export function parseVaultPackJson(
  input: string,
  current?: VaultPack | null,
  options?: VaultNormalizeOptions
): VaultPack {
  let parsed: unknown;
  try {
    parsed = JSON.parse(input);
  } catch {
    throw new Error('That file is not valid JSON. Upload a .json file or a .txt file containing JSON.');
  }

  if (!objectLike(parsed)) {
    throw new Error('A vault pack import must be one JSON object.');
  }

  return normalizeVaultPack(parsed as Partial<VaultPack>, current, options);
}

export async function readVaultPackImportFile(file: File): Promise<string> {
  if (!JSON_FILE_RE.test(file.name)) {
    throw new Error('Upload a .json file or a .txt file containing JSON.');
  }
  const textValue = await file.text();
  if (!textValue.trim()) {
    throw new Error('That file is empty. Upload a JSON vault pack definition.');
  }
  return textValue;
}
